import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concat, exhaustMap, map, Observable, of } from 'rxjs';

import { SupplierActions } from '@ps/survey-data-access';

import { SurveyCreationAudienceActions } from '../audience/survey-creation-audience.actions';
import { SurveyCreationCoreActions } from '../core/survey-creation-core.actions';
import { saveSucceeded$ } from '../core/utils/save-outcome.helper';
import { selectIsSurveySaveable } from '../core/utils/survey-save-pipeline.helper';
import { SurveyCreationInsightsActions } from './survey-creation-insights.actions';
import { OfferPriceApply } from './utils/survey-creation-apply-offer-price.types';

// Always refreshes recalculation; never announces the drawer's own success state is the feedback.
const SAVE_AFTER_CPI_APPLY = SurveyCreationCoreActions.saveSurvey({
  recalculationsEnabled: true,
  silent: true,
});

@Injectable()
export class SurveyCreationApplyOfferPriceEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  public readonly applyOfferPrice$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.applyOfferPrice),
      concatLatestFrom(() => this._store.select(selectIsSurveySaveable)),
      exhaustMap(([apply, isSaveable]) =>
        isSaveable ? this._applyThenSave(apply) : of(SAVE_AFTER_CPI_APPLY),
      ),
    ),
  );

  private _applyThenSave({
    pricing,
    surveyId,
    allocationsUpdate,
  }: OfferPriceApply): Observable<Action> {
    return concat(
      of(SurveyCreationInsightsActions.applyCpiPricing(pricing)),
      of(
        SurveyCreationAudienceActions.setSuppliersMetaData({
          metaData: {
            defaultPurePriceOverride:
              allocationsUpdate.context.defaultPurePriceOverride,
          },
        }),
      ),
      of(SAVE_AFTER_CPI_APPLY),
      saveSucceeded$(this._actions$).pipe(
        map(() =>
          SupplierActions.updateManagedAllocations({
            surveyId,
            update: allocationsUpdate,
          }),
        ),
      ),
    );
  }
}
